/**
 * WelcomeToursPrompt — soft-dismissable welcome modal that auto-shows on
 * first login for users with admin / test-admin / super-admin rights so
 * they don't miss the interactive page tours. Records "seen" in
 * localStorage on any close path (button, Escape, outside-click).
 *
 * UX (per user-choice 4b "soft"):
 *  - Two CTAs: "View all tours" (opens AllToursModal) and "Skip for now"
 *  - Escape and backdrop-click dismiss the prompt and mark it seen
 *  - Suppressed on the public `/login`, `/whats-new`, `/conv-link` and
 *    `/tours` routes so we don't double-stack overlays
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkle, X, BookOpen, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import AllToursModal from "@/components/AllToursModal";
import {
  TOURS,
  WELCOME_PROMPT_SEEN_KEY,
  userCanSeeTour,
  hasSeenTour,
  fetchTourProgress,
  isWelcomePromptDismissed,
} from "@/lib/tours";

export default function WelcomeToursPrompt({ user }) {
  const [open, setOpen] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [serverSeen, setServerSeen] = useState([]);
  const { pathname } = useLocation();

  // Decide whether to show on each user / route change.
  useEffect(() => {
    if (!user?.email) { setOpen(false); return; }

    // Suppress on auth / public landing routes.
    const suppressed =
      pathname === "/login" ||
      pathname === "/tours" ||
      pathname.startsWith("/whats-new") ||
      pathname.startsWith("/conv-link") ||
      pathname.startsWith("/auth/callback");
    if (suppressed) return;

    // Only show to staff who actually have access to at least one tour.
    const eligible = TOURS.some((t) => userCanSeeTour(user, t));
    if (!eligible) return;

    // iter150X — accept multiple dismiss aliases + ?dismiss_tours=1
    // URL flag so automated tests have a stable, documented way to
    // suppress this overlay.
    if (isWelcomePromptDismissed()) return;

    setOpen(true);
  }, [user, pathname]);

  // iter146d — pull cross-device progress so the modal can show how
  // many tours this user has already finished from any device.
  useEffect(() => {
    if (!user?.email) return;
    let cancelled = false;
    (async () => {
      const data = await fetchTourProgress();
      if (!cancelled && data?.seen) setServerSeen(data.seen);
    })();
    return () => { cancelled = true; };
  }, [user?.email, open]);

  // Escape closes (records seen).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dismiss = () => {
    try { window.localStorage.setItem(WELCOME_PROMPT_SEEN_KEY, "1"); } catch {}
    setOpen(false);
  };

  const openCatalog = () => {
    // Mark the welcome prompt as seen once the user engages with the catalog.
    try { window.localStorage.setItem(WELCOME_PROMPT_SEEN_KEY, "1"); } catch {}
    setOpen(false);
    setShowCatalog(true);
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[750] flex items-center justify-center p-4"
             data-testid="welcome-tours-prompt">
          <div className="absolute inset-0 bg-slate-900/55"
               data-testid="welcome-tours-backdrop"
               onClick={dismiss} />
          <div className="relative w-full max-w-md bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-br from-indigo-600 via-blue-700 to-emerald-600 px-5 py-6 text-white">
              <div className="flex items-center gap-2 mb-2">
                <Sparkle size={20} weight="fill" />
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-90">Welcome aboard</span>
              </div>
              <h2 className="text-xl font-bold font-['Chivo'] tracking-tight">
                Take a quick tour of the heavy hitters
              </h2>
              <p className="text-xs text-white/85 mt-2 leading-relaxed">
                We've prepared {TOURS.length} short interactive walkthroughs for the
                deepest admin workflows — Requested Changes, Evidence Rules,
                Warranty Chat, and the Test Queue. Each one takes under a minute.
              </p>
            </div>

            <div className="px-5 py-5 space-y-3">
              {/* iter146d — Completion progress bar. Combines the
                  cross-device backend `seen` set with the local
                  flags so a user who finished tours on a different
                  device still sees their progress here. */}
              {(() => {
                const visibleTours = TOURS.filter((t) => userCanSeeTour(user, t));
                const merged = new Set([...(serverSeen || [])]);
                for (const t of visibleTours) if (hasSeenTour(t.key)) merged.add(t.key);
                const completed = visibleTours.filter((t) => merged.has(t.key)).length;
                const total = visibleTours.length;
                const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
                return (
                  <div data-testid="welcome-tours-progress"
                       className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-600">
                      <CheckCircle size={11} weight="fill" className={completed > 0 ? "text-emerald-600" : "text-slate-400"} />
                      <span>Your progress</span>
                      <span className="ml-auto text-slate-700"
                            data-testid="welcome-tours-progress-text">
                        {completed} / {total} tours watched
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-200 overflow-hidden">
                      <div data-testid="welcome-tours-progress-bar"
                           className="h-full bg-gradient-to-r from-indigo-500 via-blue-600 to-emerald-500 transition-[width] duration-500"
                           style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })()}

              <ul className="text-xs text-slate-700 space-y-1.5">
                {TOURS.filter((t) => userCanSeeTour(user, t)).map((t) => {
                  const done = hasSeenTour(t.key) || (serverSeen || []).includes(t.key);
                  return (
                    <li key={t.key} className="flex items-start gap-2"
                        data-testid={`welcome-tours-item-${t.key}`}>
                      {done
                        ? <CheckCircle size={12} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                        : <BookOpen size={12} weight="bold" className="text-blue-700 mt-0.5 shrink-0" />}
                      <span><b>{t.title}</b> — {t.description}</span>
                    </li>
                  );
                })}
              </ul>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <button onClick={dismiss}
                        data-testid="welcome-tours-skip"
                        className="text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 px-2 py-1">
                  Skip for now
                </button>
                <button onClick={openCatalog}
                        data-testid="welcome-tours-open"
                        className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-blue-700 text-white hover:bg-blue-800 text-xs font-bold uppercase tracking-wider">
                  View all tours <ArrowRight size={11} weight="bold" />
                </button>
              </div>
            </div>

            <button onClick={dismiss}
                    data-testid="welcome-tours-close"
                    title="Dismiss"
                    className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-sm text-white/80 hover:bg-white/15">
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      <AllToursModal open={showCatalog} onClose={() => setShowCatalog(false)} user={user} />
    </>
  );
}
